import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Landmark } from "lucide-react";
import { COMPANY } from "../lib/company";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FadeUp } from "../components/Shared";
import { FileUpload } from "../components/FileUpload";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["General Enquiry", "Request for Quote (RFQ)", "Partnership"];

const INFO = [
  { icon: Mail, label: "Email", value: "vectronglobal@gmail.com" },
  { icon: MapPin, label: "Registered Office", value: `${COMPANY.addressLines[0]}, ${COMPANY.addressLines[1]}` },
  { icon: Landmark, label: "GSTIN", value: `${COMPANY.legalName} · ${COMPANY.gstin}` },
  { icon: Clock, label: "Response Time", value: "Within 24 business hours" },
];

const initialForm = { name: "", email: "", company: "", category: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.category || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/enquiries/contact`, { ...form, file_ids: files.map((f) => f.id) });
      toast.success("Message sent! Our team will get back to you within 24 hours.");
      setForm(initialForm);
      setFiles([]);
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="py-28 sm:py-36 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary mb-6 font-mono-tech">Contact Us</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[1.05]">
              Let's talk about <span className="font-medium text-primary">your next project.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              RFQs, partnerships or general questions — send us a message and the right team will respond.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <div className="space-y-6">
              {INFO.map((c) => (
                <div key={c.label} className="flex gap-5 p-6 rounded-xl bg-card border border-border" data-testid={`contact-info-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="h-11 w-11 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <c.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{c.label}</p>
                    <p className="mt-1 text-sm font-medium">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-8">
            <form onSubmit={submit} className="space-y-6 p-8 sm:p-10 rounded-xl bg-card border border-border" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="c-name">Name *</Label>
                  <Input id="c-name" value={form.name} onChange={set("name")} placeholder="Full name" data-testid="contact-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-company">Company</Label>
                  <Input id="c-company" value={form.company} onChange={set("company")} placeholder="Company name" data-testid="contact-company-input" />
                </div>
                <div className="space-y-2">
                  <Label>Enquiry Category *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="contact-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`contact-category-option-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-message">Message *</Label>
                <Textarea id="c-message" rows={6} value={form.message} onChange={set("message")} placeholder="Tell us about your project, parts, quantities and timelines..." data-testid="contact-message-input" />
              </div>
              <FileUpload files={files} onChange={setFiles} />
              <Button type="submit" size="lg" disabled={loading} className="rounded-full px-10" data-testid="contact-submit-btn">
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
